/**
 * Helpers: Format
 * ------------------------------------------------------------------------------
 * - Collection of helper functions related to formatting.
 */
/**
 * Get a formatted image size URL from Shopify's CDN.
 * @example:
 *  getSizedImageUrl('full/path/to/image/url', `100x`),
 * @param {String} src - Image URL.
 * @param {String} size - Image size filter.
 * @returns {String}
 */
export function getSizedImageUrl(src, size) {
  if (size === null || size === 'master') {
    return src
  }

  const match = src?.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i)

  if (match) {
    const prefix = src.split(match[0])
    const suffix = match[0]

    return `${prefix[0]}_${size}${suffix}`
  }

  return null
}

/**
 * Get a formatted image size URL from Cloudinary's CDN.
 * - Notes: This should perhaps be refactored to:
 *   - Use 'q_auto', 'f_auto' regardless, instead of passing in every time
 *   - Set baseUrl in the function itself, instead of passing it in every time
 * @example:
 *
 * @param {String} baseUrl - BaseUrl - Set in product-{XXX}.
 * @param {String} publicId - Folder/Filename of asset.
 * @param {Array} transformations - Array of transformations. Default to auto quality
 * @returns {String}
 */
export function getCloudinarySizedImageUrl({
  baseUrl,
  publicId,
  transformations = ['q_auto', 'f_auto'],
}) {

  // Return early if CloudinaryID isn't provided
  if (!baseUrl || !publicId) {
    return false
  }

  const cdnUrl = [baseUrl]
  cdnUrl.push(transformations.join(','))
  cdnUrl.push(encodeURI(publicId))

  return cdnUrl.join('/')
}

/**
 * Get the themes Cloudinary Base Url. Amend based on media type
 * @param {String} mediaType
 * @returns {String}
 */
export function getCloudinaryBaseUrl({ mediaType = 'image' } = false) {

  /**
   * If passing a video in, change `mediaType` to `video`.
   */
  const baseUrl = mint.urls.cloudinary.replace('%TYPE%', mediaType)
  return baseUrl
}

/**
 * Format a price value based on the user's currency and locale.
 * @param {Number|String} price - Price value
 * @param {String} currency - Currency code, defined from global state, or custom
 * @param {Boolean} isCents - Price value is in cents. If £12, then '1200', else '12.00'.
 * - Boolean added as PLP / Legacy product cards take values as '12.00'
 * @returns {String|Boolean}
 */
export function formatLocalizedPrice(price, currency = mint.shop.currency, isCents = true) {

  /**
   * Create the locale from the store's default language and country values.
   * - This ensures Intl.NumberFormat can return bespoke currency symbols.
   * - Example: CAD will return `CA $`, USD will return `US $` and not just `$`.
   */
  const locale = 'en-GB'

  /**
   * Create the NumberFormat object for formatting and pass the price.
   */
  const priceValue = isCents ? Number(price / 100) : price

  let formattedPrice = new Intl.NumberFormat(
    locale,
    {
      currency,
      currencyDisplay: 'symbol',
      style: 'currency',
    },
  ).format(priceValue)

  /**
   * For Romanian currency, Liquid money filter will return 'lei' not 'RON'
   * - It's easier to adjust this function to return 'lei' than to modify
   *   the Cart and the PDP to return 'RON'.
   */
  if (currency === 'RON') {
    formattedPrice = `${formattedPrice.replace('RON', '')} lei`.trim()
  }

  return formattedPrice ? formattedPrice : false
}

/**
 * Generate a srcset attribute for an image object.
 * @param {Object} image - Formatted image object.
 * @param {String} [minMax] - Min and max size for srcset in `min-max` format.
 * @returns {Object}
 */
export function getImageSrcset(
  image = {},
  minMax = '200-1000',
) {

  if (!image.src) {
    return {
      srcset: null,
    }
  }

  const min = Number(minMax.split('-')[0])
  const max = Number(minMax.split('-')[1])

  const range = max - min
  const increment = range / 5

  const array = [...Array(5)].map((_item, index) => {
    const step = increment * (index + 1)
    const value = min + step

    return value
  })

  const srcset = array.map((step) => {
    const roundedSize = Math.ceil(step)
    let sizedImageUrl

    if (image.source == 'cloudinary') {

      const baseUrl = image.baseUrl
      const publicId = image.publicId

      /// Cloudinary Transformations
      /// q_auto - optimal balance between file size and visual quality
      /// f_auto - Optimal file format selection based on browser compatibility
      /// c_scale - Scale the Image
      /// w_{Size} - Requested Image width
      const transformations = [
        'q_auto', 'f_auto', 'c_scale', `w_${roundedSize}`,
      ]

      sizedImageUrl = getCloudinarySizedImageUrl({ baseUrl, publicId, transformations })
    } else {
      sizedImageUrl = getSizedImageUrl(image.src, `${roundedSize}x`)
    }
    return `${sizedImageUrl} ${roundedSize}w`
  })

  return {
    array,
    max,
    min,
    placeholder: (min / 2),
    srcset,
  }
}

/**
 * Reverses the order of variant title for dual sizes.
 * - e.g 'Regular / 12' -> '12 / Regular'
 * @param {string} variantTitle - The variant title.
 * @returns {string} - The formatted variant title.
 */
export function formatVariantTitle(variantTitle) {
  if (!variantTitle || !variantTitle.includes(' / ')) {
    return variantTitle
  }

  const [firstPart, secondPart] = variantTitle.split(' / ')

  return `${secondPart} / ${firstPart}`
}

/**
 * Formats a date object according to the provided format string.
 * @param {Date} date The date object to format.
 * @param {string} format The Ruby's `strftime` format string specifying how to format the date.
 * @see [reference](https://www.strfti.me/)
 *
 * @example
 * const date = new Date();
 * const formattedDate = strftime(date, '%A %d %B %Y, %I:%M:%S %p');
 * console.log(formattedDate); // Outputs something like "Friday 07 May 2024, 03:42:17 PM"
 *
 * @returns {string} The formatted date string.
 */
export function strftime(date, format) {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ]
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  return format.replace(/%([a-zA-Z])/g, (string, specifier) => {
    switch (specifier) {
      case 'A':
        return days[date.getDay()]
      case 'a':
        return days[date.getDay()].substring(0, 3)
      case 'B':
        return months[date.getMonth()]
      case 'b':
        return months[date.getMonth()].substring(0, 3)
      case 'Y':
        return date.getFullYear().toString()
      case 'y':
        return date.getFullYear().toString().substring(2)
      case 'm':
        return (date.getMonth() + 1).toString().padStart(2, '0')
      case 'd':
        return date.getDate().toString().padStart(2, '0')
      case 'e':
        return date.getDate().toString()
      case 'j': {
        const startOfYear = new Date(date.getFullYear(), 0, 0)
        const diff = date - startOfYear
        const oneDay = 1000 * 60 * 60 * 24
        return Math.floor(diff / oneDay)
          .toString()
          .padStart(3, '0')
      }
      case 'U':
      case 'W': {
        // Calculate the week number
        const oneWeek = 1000 * 60 * 60 * 24 * 7
        const startOfYear = new Date(date.getFullYear(), 0, 0)
        const diff = date - startOfYear
        const weekNumber = Math.floor(diff / oneWeek)
        if (specifier === 'U') {
          return (weekNumber + 1).toString().padStart(2, '0')
        } else {
          // Adjust for first Monday as the first day of the first week
          const dayOfWeek = startOfYear.getDay()
          let weekNumberAdjusted = weekNumber
          if (dayOfWeek > 0) {
            weekNumberAdjusted++
          }
          return weekNumberAdjusted.toString().padStart(2, '0')
        }
      }
      case 'w':
        return date.getDay().toString()
      case 'x':
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
      case 'H':
        return date.getHours().toString().padStart(2, '0')
      case 'I':
        return (((date.getHours() + 11) % 12) + 1).toString().padStart(2, '0')
      case 'l':
        return (((date.getHours() + 11) % 12) + 1).toString()
      case 'M':
        return date.getMinutes().toString().padStart(2, '0')
      case 'N':
        return date.getMilliseconds().toString().padStart(3, '0')
      case 'p':
        return date.getHours() < 12 ? 'am' : 'pm'
      case 'P':
        return date.getHours() < 12 ? 'AM' : 'PM'
      case 'S':
        return date.getSeconds().toString().padStart(2, '0')
      case 'X':
        return `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`
      case 'Z':
        return new Intl.DateTimeFormat('en', { timeZoneName: 'short' })
          .formatToParts(date)
          .find((part) => part.type === 'timeZoneName').value
      case 'c':
        return date.toString()
      case '%':
        return '%'
      default:
        return specifier
    }
  })
}
